import React, { useMemo, useState } from "react";

import {
  setFootprintingBeefPrimaryDataAdmin,
  setFootprintingCropPrimaryDataAdmin,
  setFootprintingDairyPrimaryDataAdmin,
  setFootprintingFarmPrimaryDataAdmin,
  setFootprintingPigletPrimaryDataAdmin,
  setFootprintingPorkPrimaryDataAdmin,
} from "services";

import {
  getYearArray,
  isPercentageComplete,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingBeefFootprints,
  useGetFootprintingCropFootprints,
  useGetFootprintingDairyFootprints,
  useGetFootprintingFarmFootprints,
  useGetFootprintingPigletFootprints,
  useGetFootprintingPorkFootprints,
  useGetPracticeCrops,
  useNavigate,
} from "utils";

import { ENUM_FOOTPRINT_ASSESSMENT_TYPE } from "consts";

import { FootprintingFarmPrimaryData } from "models";

import { Button, Flex, PageSpin, Popconfirm, SelectSegmented, Table, Tag, Typography } from "components";

import FootprintingPdfExport from "pages/admin/verifications/components/footprinting/FootprintingPdfExport";

interface FootprintingVerificationsAssessmentListProps {
  farmId: string;
}

const FootprintingVerificationsAssessmentList = ({ farmId }: FootprintingVerificationsAssessmentListProps) => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const [showPdfExport, setShowPdfExport] = useState(false);

  const cropDetailsQuery = useGetPracticeCrops({ isAdmin: true });
  const crops = cropDetailsQuery.data;
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);

  const availableYears = getYearArray(carbonProgram?.farmLevelFootprinting?.startYear ?? currentYear, currentYear);
  const farmProgressQueries = useGetFootprintingFarmFootprints(farmId, availableYears);
  const diaryProgressQueries = useGetFootprintingDairyFootprints(farmId, availableYears);
  const cropProgressQueries = useGetFootprintingCropFootprints(farmId, availableYears, carbonProgram?.farmLevelFootprinting?.programCrops);
  const beefProgressQueries = useGetFootprintingBeefFootprints(farmId, availableYears);
  const porkProgressQueries = useGetFootprintingPorkFootprints(farmId, availableYears);
  const pigletProgressQueries = useGetFootprintingPigletFootprints(farmId, availableYears);

  // Tagged explicitly (rather than re-derived from `data` shape below) since crop/dairy/beef/pork/piglet footprints
  // aren't reliably distinguishable by their fields alone (e.g. dairy/beef/pork/piglet all carry feedProductionCompletion).
  const allQueries = [
    ...cropProgressQueries.map((query) => ({ query, fpType: "crop" as ENUM_FOOTPRINT_ASSESSMENT_TYPE })),
    ...farmProgressQueries.map((query) => ({ query, fpType: "farm" as ENUM_FOOTPRINT_ASSESSMENT_TYPE })),
    ...diaryProgressQueries.map((query) => ({ query, fpType: "dairy" as ENUM_FOOTPRINT_ASSESSMENT_TYPE })),
    ...beefProgressQueries.map((query) => ({ query, fpType: "beef" as ENUM_FOOTPRINT_ASSESSMENT_TYPE })),
    ...porkProgressQueries.map((query) => ({ query, fpType: "pork" as ENUM_FOOTPRINT_ASSESSMENT_TYPE })),
    ...pigletProgressQueries.map((query) => ({ query, fpType: "piglet" as ENUM_FOOTPRINT_ASSESSMENT_TYPE })),
  ];
  const isLoading = allQueries.some(({ query }) => queryIsLoading(query));

  const yearsWithCompleteFootprints = useMemo(
    () =>
      availableYears.filter((year) => {
        const farmComplete = farmProgressQueries.some((fpq) => fpq.data?.year === year && isPercentageComplete(fpq.data?.completionPct ?? 0));
        const atLeastOneCropComplete = cropProgressQueries.some((cpq) => cpq.data?.year === year && isPercentageComplete(cpq.data?.completionPct ?? 0));
        const dairyComplete = diaryProgressQueries.some((dpq) => dpq.data?.year === year && isPercentageComplete(dpq.data?.completionPct ?? 0));
        return farmComplete && (atLeastOneCropComplete || dairyComplete);
      }),
    [availableYears, farmProgressQueries, cropProgressQueries, diaryProgressQueries]
  );

  const [selectedExportYear, setSelectedExportYear] = useState<number | null>(yearsWithCompleteFootprints[0] ?? null);

  const hasCompletedFootprints = yearsWithCompleteFootprints.length > 0;

  if (isLoading) return <PageSpin />;

  const columns = [
    { title: "Year", dataIndex: "year" },
    { title: "Assessment", dataIndex: "assessment" },
    { title: "Percentage Complete", dataIndex: "completionPct" },
    { title: "Submitted", dataIndex: "isSubmitted" },
    { title: "Verified", dataIndex: "isVerified" },
    { title: "Actions", dataIndex: "actions" },
  ];

  // The admin PUT rejects (422) any field that differs from the stored row of a verified footprint, so send only the
  // required farmId/year (and cropId for crops) plus the one flag being changed, never cached copies of other fields.
  const setFootprintData = (
    cardType: ENUM_FOOTPRINT_ASSESSMENT_TYPE,
    primaryDataId: string,
    data: Pick<FootprintingFarmPrimaryData, "farmId" | "year" | "isSubmitted" | "isVerified">,
    cropId?: string
  ) => {
    if (cardType === "crop" && cropId) {
      cropProgressQueries.forEach((q) => q.refetch());
      setFootprintingCropPrimaryDataAdmin({ ...data, cropId }, primaryDataId).then(() => {
        cropProgressQueries.forEach((q) => q.refetch());
      });
    } else if (cardType === "dairy") {
      diaryProgressQueries.forEach((q) => q.refetch());
      setFootprintingDairyPrimaryDataAdmin(data, primaryDataId).then(() => {
        diaryProgressQueries.forEach((q) => q.refetch());
      });
    } else if (cardType === "beef") {
      beefProgressQueries.forEach((q) => q.refetch());
      setFootprintingBeefPrimaryDataAdmin(data, primaryDataId).then(() => {
        beefProgressQueries.forEach((q) => q.refetch());
      });
    } else if (cardType === "pork") {
      porkProgressQueries.forEach((q) => q.refetch());
      setFootprintingPorkPrimaryDataAdmin(data, primaryDataId).then(() => {
        porkProgressQueries.forEach((q) => q.refetch());
      });
    } else if (cardType === "piglet") {
      pigletProgressQueries.forEach((q) => q.refetch());
      setFootprintingPigletPrimaryDataAdmin(data, primaryDataId).then(() => {
        pigletProgressQueries.forEach((q) => q.refetch());
      });
    } else {
      farmProgressQueries.forEach((q) => q.refetch());
      setFootprintingFarmPrimaryDataAdmin(data, primaryDataId).then(() => {
        // Refetch all since unsubmission will also change crops/dairy/beef/pork/piglet/etc.
        cropProgressQueries.forEach((q) => q.refetch());
        farmProgressQueries.forEach((q) => q.refetch());
        diaryProgressQueries.forEach((q) => q.refetch());
        beefProgressQueries.forEach((q) => q.refetch());
        porkProgressQueries.forEach((q) => q.refetch());
        pigletProgressQueries.forEach((q) => q.refetch());
      });
    }
  };

  // Unsubmitting the farm also unsubmits that year's other footprints, so it must not reach a verified one
  const yearsWithVerifiedFootprints = new Set(allQueries.filter(({ query }) => query.data?.footprint?.isVerified).map(({ query }) => query.data?.year));

  const data = allQueries
    .map(({ query, fpType: FP_TYPE }) => {
      const { data } = query;
      if (!data || !data.footprint?.id) return null;

      // @ts-expect-error I know that only crop FPs have cropId, that's the point
      const { cropId } = data;

      const fpIsComplete = isPercentageComplete(data.completionPct);
      const primaryDataId = data.footprint.id;
      const requiredFields = { farmId: data.footprint.farmId, year: data.footprint.year };
      return {
        key: data.footprint?.id,
        sortValue: data.year - (FP_TYPE === "crop" ? 0 : 0.1),
        year: data.year,
        assessment: `${FP_TYPE === "crop" ? crops?.find((c) => c.id === cropId)?.name.toUpperCase() : FP_TYPE}`,
        completionPct: `${(data.completionPct * 100).toFixed(0)}%`,
        isSubmitted: data.footprint?.isSubmitted ? <Tag text="Submitted" colour="green-200" /> : <Tag text="Not submitted" colour="orange" />,
        isVerified: data.footprint?.isVerified ? <Tag text="Verified" colour="green-200" /> : <Tag text="Not verified" colour="orange" />,
        actions: (
          <Flex gap="1rem">
            <Button
              style={{ width: "fit-content" }}
              type="primary"
              disabled={!fpIsComplete || !data.footprint.isSubmitted}
              onClick={() => setFootprintData(FP_TYPE, primaryDataId, { ...requiredFields, isVerified: !data.footprint?.isVerified }, cropId)}
            >
              {data.footprint?.isVerified === true ? "Unverify" : "Mark Verified"}
            </Button>
            <Button
              style={{ width: "fit-content" }}
              type="secondary"
              onClick={() =>
                navigate(
                  `/verifications/farms/${farmId}/footprinting/${FP_TYPE}/${data.footprint?.id}${
                    FP_TYPE === "dairy" || FP_TYPE === "beef" || FP_TYPE === "pork" || FP_TYPE === "piglet" ? `?year=${data.footprint?.year}` : ""
                  }`
                )
              }
            >
              See Details
            </Button>
            {FP_TYPE === "farm" ? (
              <Popconfirm
                title="ARE YOU SURE"
                description="This will force the farmer to log in and resubmit their data"
                onConfirm={() => setFootprintData(FP_TYPE, primaryDataId, { ...requiredFields, isSubmitted: false }, cropId)}
              >
                <Button
                  style={{ width: "fit-content" }}
                  type="primary"
                  disabled={!fpIsComplete || !data.footprint.isSubmitted || yearsWithVerifiedFootprints.has(data.year)}
                >
                  {data.footprint?.isSubmitted === true ? `Unsubmit ${data.footprint.year}` : "Waiting for submission"}
                </Button>
              </Popconfirm>
            ) : null}
          </Flex>
        ),
      };
    })
    .filter((row) => !!row)
    .sort((a, b) => b.sortValue - a.sortValue);

  const handleExportClick = () => {
    if (yearsWithCompleteFootprints.length === 1) {
      setSelectedExportYear(yearsWithCompleteFootprints[0]);
      setShowPdfExport(true);
    } else if (selectedExportYear && yearsWithCompleteFootprints.includes(selectedExportYear)) {
      setShowPdfExport(true);
    }
  };

  const handleExportClose = () => {
    setShowPdfExport(false);
  };

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" align="center">
        <Typography variant="h4">Footprinting Assessments</Typography>
        <Flex gap={8} align="center">
          {!!yearsWithCompleteFootprints.length && (
            <SelectSegmented
              placeholder="Select year"
              value={selectedExportYear}
              onChange={(value) => setSelectedExportYear(value as number)}
              options={yearsWithCompleteFootprints.map((year) => ({ label: year.toString(), value: year }))}
              style={{ width: 120 }}
            />
          )}
          <Button
            type="secondary"
            loading={showPdfExport}
            disabled={showPdfExport || !hasCompletedFootprints || (yearsWithCompleteFootprints.length > 1 && !selectedExportYear)}
            onClick={handleExportClick}
          >
            Export PDF
          </Button>
        </Flex>
      </Flex>
      <Table data={data} columns={columns} rowKey={(r) => r.key} />
      {showPdfExport && selectedExportYear && (
        <FootprintingPdfExport
          farmId={farmId}
          year={selectedExportYear}
          availableYears={availableYears}
          farmProgressQueries={farmProgressQueries}
          cropProgressQueries={cropProgressQueries}
          dairyProgressQueries={diaryProgressQueries}
          cropDetails={cropDetailsQuery.data}
          isCropDetailsLoading={cropDetailsQuery.isLoading}
          onClose={handleExportClose}
        />
      )}
    </Flex>
  );
};

export default FootprintingVerificationsAssessmentList;
